import React, { useEffect, useState, useRef } from 'react'
import TopBanner from '../../../components/topBanner/topBanner'
import imgBanner from "../../../images/banner/banner-tin-tuc.jpg"
import TitleCenter from '../../../components/title/TitleCenter'
import { getRecruitmentsByLanguage } from '../../../apis/recruitments';
import { submitCandidateCv, uploadCandidateCvFile } from '../../../apis/adminRecruitmentApplications';
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '../../../utils/localeDataLoader';
import { FiUser, FiPhone, FiMail, FiPaperclip, FiFileText, FiSend, FiCheckCircle, FiX } from 'react-icons/fi';

function RecruimentContent() {
    const { slug } = useParams();
    const [data, setData] = useState({});
    const [jobId, setJobId] = useState(null);
    const [allowCv, setAllowCv] = useState(true);
    const [cvForm, setCvForm] = useState({ fullName: '', email: '', phone: '', coverLetterText: '' });
    const [cvFile, setCvFile] = useState(null);
    const [coverLetterMode, setCoverLetterMode] = useState('text'); // 'text' | 'file'
    const [coverLetterFile, setCoverLetterFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittedSuccess, setSubmittedSuccess] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const idRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        idRef.current = null;
    }, [slug]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const lang = normalizeLang(i18n.language);
                const response = await getRecruitmentsByLanguage(lang);
                if (response && response.data && response.data.items) {
                    let filteredData = idRef.current 
                        ? response.data.items.find(item => item.id === idRef.current)
                        : response.data.items.find(item => item.slug === slug);

                    if (!filteredData) {
                        filteredData = response.data.items.find(item => item.slug === slug);
                    }

                    if (filteredData) {
                        const expectedBasePath = lang === 'vi'
                            ? 'tuyen-dung/cong-viec-tuyen-dung'
                            : 'recruitment/career-opportunities';
                        const expectedPath = `/${expectedBasePath}/${filteredData.slug}`;

                        if (location.pathname !== expectedPath) {
                            navigate(expectedPath, { replace: true });
                        }

                        idRef.current = filteredData.id;
                        setJobId(filteredData.id);
                        setAllowCv(filteredData.allowCvSubmission !== false);
                        setData({
                            location: filteredData.title,
                            description: filteredData.description
                        });
                    }
                }
            } catch (error) {
                console.log("Failed to fetch recruitment details", error);
            }
        };
        fetchData();
    }, [slug, i18n.language, location.pathname, navigate]);

    const validateSelectedFile = (file) => {
        if (!file) return false;
        const maxBytes = 3 * 1024 * 1024; // 3MB
        const allowedExts = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.pdf'];
        const forbiddenChars = /['\/\[\]*<>;":?|]/;
        const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

        if (!allowedExts.includes(ext)) {
            alert(t('recruitment.err_invalid_file'));
            return false;
        }

        if (file.size > maxBytes) {
            alert(t('recruitment.err_file_size'));
            return false;
        }

        if (forbiddenChars.test(file.name)) {
            alert(t('recruitment.err_file_chars'));
            return false;
        }

        return true;
    };

    const handleCvFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selected = e.target.files[0];
            if (validateSelectedFile(selected)) {
                setCvFile(selected);
            } else {
                e.target.value = null;
                setCvFile(null);
            }
        }
    };

    const handleCoverFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selected = e.target.files[0];
            if (validateSelectedFile(selected)) {
                setCoverLetterFile(selected);
            } else {
                e.target.value = null;
                setCoverLetterFile(null);
            }
        }
    };

    const handleCvSubmit = async (e) => {
        e.preventDefault();
        if (!cvFile) {
            alert(t('recruitment.err_select_cv'));
            return;
        }
        try {
            setIsSubmitting(true);
            const uploadCvRes = await uploadCandidateCvFile(cvFile);
            const cvFileUrl = uploadCvRes?.data?.fileUrl || uploadCvRes?.data?.url || '';

            let finalCoverLetter = cvForm.coverLetterText;
            if (coverLetterMode === 'file' && coverLetterFile) {
                const uploadCoverRes = await uploadCandidateCvFile(coverLetterFile);
                const coverUrl = uploadCoverRes?.data?.fileUrl || uploadCoverRes?.data?.url || '';
                finalCoverLetter = `[Tệp đính kèm] ${coverUrl}`;
            }

            const payload = {
                recruitmentId: jobId ? Number(jobId) : null,
                fullName: cvForm.fullName,
                email: cvForm.email,
                phone: cvForm.phone,
                coverLetter: finalCoverLetter,
                cvFileUrl,
                status: 'pending',
            };

            await submitCandidateCv(payload);
            setSubmittedSuccess(true);
        } catch (err) {
            alert(err?.message || t('recruitment.err_submit_fail'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const formattedHTML = { __html: data.description };

    return (
        <div className='recruimentContent content_pages'>
            <TopBanner
                titleCenter={t('recruitment.title')}
                imgBanner={imgBanner}
                pageName={t('recruitment.title')}
                text={data.location}
            />
            <div className='layout_container'>
                <TitleCenter text={data.location} />
                <div className="recruiment-content">
                    <div className="quill-content" dangerouslySetInnerHTML={formattedHTML}></div>
                    
                    {allowCv && (
                        <div className="recruitment-apply-bar">
                            <button className="open-apply-modal-btn" onClick={() => { setSubmittedSuccess(false); setIsModalOpen(true); }}>
                                <FiSend size={20} /> {t('recruitment.apply_now')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {allowCv && isModalOpen && (
                <div className="cv-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="cv-modal-card" onClick={(e) => e.stopPropagation()}>
                        <button className="cv-modal-close" onClick={() => setIsModalOpen(false)} title="Đóng">
                            <FiX size={22} />
                        </button>
                        <div className="recruitment-cv-header">
                            <div className="cv-badge">{t('recruitment.badge_opportunity')}</div>
                            <h3>🚀 {t('recruitment.apply_now')}</h3>
                            <p>{t('recruitment.apply_subtitle')}</p>
                        </div>
                        {submittedSuccess ? (
                            <div className="recruitment-cv-success">
                                <div className="success-icon">🎉</div>
                                <h4>{t('recruitment.success_title')}</h4>
                                <p>{t('recruitment.success_message')}</p>
                                <button type="button" className="cv-success-close-btn" onClick={() => setIsModalOpen(false)}>
                                    Hoàn tất & Đóng
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleCvSubmit} className="recruitment-cv-form">
                                <div className="cv-form-row">
                                    <div className="cv-form-group">
                                        <label>{t('recruitment.fullname_label')} <span className="req">*</span></label>
                                        <div className="cv-input-wrapper">
                                            <FiUser className="input-icon" />
                                            <input type="text" required placeholder={t('recruitment.fullname_placeholder')} value={cvForm.fullName} onChange={e => setCvForm({ ...cvForm, fullName: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="cv-form-group">
                                        <label>{t('recruitment.phone_label')} <span className="req">*</span></label>
                                        <div className="cv-input-wrapper">
                                            <FiPhone className="input-icon" />
                                            <input type="tel" required placeholder={t('recruitment.phone_placeholder')} value={cvForm.phone} onChange={e => setCvForm({ ...cvForm, phone: e.target.value })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="cv-form-group">
                                    <label>{t('recruitment.email_label')} <span className="req">*</span></label>
                                    <div className="cv-input-wrapper">
                                        <FiMail className="input-icon" />
                                        <input type="email" required placeholder={t('recruitment.email_placeholder')} value={cvForm.email} onChange={e => setCvForm({ ...cvForm, email: e.target.value })} />
                                    </div>
                                </div>

                                <div className="cv-form-group">
                                    <label>{t('recruitment.upload_cv_label')} <span className="req">*</span></label>
                                    <div className="cv-file-picker">
                                        <input type="file" required accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf" onChange={handleCvFileChange} id="cv-upload-input" style={{ display: 'none' }} />
                                        <label htmlFor="cv-upload-input" className={`cv-file-btn ${cvFile ? 'has-file' : ''}`}>
                                            {cvFile ? (
                                                <span><FiCheckCircle size={18} /> <strong>{cvFile.name}</strong> ({(cvFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                                            ) : (
                                                <span><FiPaperclip size={18} /> {t('recruitment.upload_cv_button')}</span>
                                            )}
                                        </label>
                                    </div>
                                    <div className="file-rules-hint">
                                        <p>• {t('recruitment.rule_hint_formats')}</p>
                                        <p>• {t('recruitment.rule_hint_chars')}</p>
                                    </div>
                                </div>

                                <div className="cv-form-group cover-letter-group">
                                    <div className="cover-letter-header">
                                        <label>{t('recruitment.cover_letter_label')}</label>
                                        <div className="cover-letter-toggle">
                                            <button
                                                type="button"
                                                className={`toggle-btn ${coverLetterMode === 'text' ? 'active' : ''}`}
                                                onClick={() => setCoverLetterMode('text')}
                                            >
                                                {t('recruitment.tab_type_text')}
                                            </button>
                                            <button
                                                type="button"
                                                className={`toggle-btn ${coverLetterMode === 'file' ? 'active' : ''}`}
                                                onClick={() => setCoverLetterMode('file')}
                                            >
                                                {t('recruitment.tab_type_file')}
                                            </button>
                                        </div>
                                    </div>

                                    {coverLetterMode === 'text' ? (
                                        <textarea
                                            rows="3"
                                            placeholder={t('recruitment.cover_letter_placeholder')}
                                            value={cvForm.coverLetterText}
                                            onChange={e => setCvForm({ ...cvForm, coverLetterText: e.target.value })}
                                        ></textarea>
                                    ) : (
                                        <div className="cv-file-picker">
                                            <input type="file" accept=".pdf,.doc,.docx" onChange={handleCoverFileChange} id="cover-upload-input" style={{ display: 'none' }} />
                                            <label htmlFor="cover-upload-input" className={`cv-file-btn ${coverLetterFile ? 'has-file' : ''}`}>
                                                {coverLetterFile ? (
                                                    <span><FiCheckCircle size={16} /> <strong>{coverLetterFile.name}</strong> ({(coverLetterFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                                                ) : (
                                                    <span><FiPaperclip size={16} /> {t('recruitment.upload_cover_button')}</span>
                                                )}
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <button type="submit" disabled={isSubmitting} className="cv-submit-btn">
                                    {isSubmitting ? t('recruitment.submitting_btn') : <><FiSend size={18} /> {t('recruitment.submit_btn')}</>}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default RecruimentContent
